import {
  useCallback,
  useMemo,
  useState,
  type ComponentType,
  type Dispatch,
  type SetStateAction,
} from "react";
import { useSearchParams } from "react-router-dom";
import {
  Button,
  DeleteModal,
  InputBase,
  Modal,
  Table,
} from "@telluscompany/design-system";
import { PlusCircle } from "lucide-react";

import { useLocalization } from "@i18n";
import { useUpdateTableUserPreferences } from "@shared/hooks";
import { Gs3TablePaginator, readOnlyInputClass } from "@shared/components";
import { useUserContext } from "@shared/contexts";
import {
  EMPTY_PAGINATION_META,
  parsePageSize,
} from "@shared/constants/pagination.constants";

import { useAdminStructureRaisingHistoriesQuery } from "../../api/structures/structures.queries";
import {
  useCreateAdminStructureRaisingHistoryMutation,
  useDeleteAdminStructureRaisingHistoryMutation,
  usePatchAdminStructureRaisingHistoryMutation,
} from "../../api/structures/structures.mutations";
import type {
  AdminStructureRaisingHistoryFilters,
  AdminStructureRaisingHistoryListItem,
  DamRaisingMethod,
} from "../../api/structures/structures.types";
import type { AdminStructureRaisingHistoryForm as RaisingHistoryFormValues } from "../../schemas";
import { AdminStructureRaisingHistoryFilter } from "./AdminStructureRaisingHistoryFilter";
import { AdminStructureRaisingHistoryActionMenu } from "./AdminStructureRaisingHistoryActionMenu";
import { AdminStructureRaisingHistoryForm } from "./AdminStructureRaisingHistoryForm";

const RAISING_HISTORY_PAGE_PARAM = "raisingHistoryPage";
const RAISING_HISTORY_LIMIT_PARAM = "raisingHistoryLimit";
const RAISING_HISTORY_TABLE_ID = "admin-structure-raising-history-table";
const DEFAULT_PAGE = 1;

type RaisingHistoryTableSort = {
  key: string;
  direction: string;
};

const RAISING_HISTORY_COLUMN_TO_API_SORT: Record<string, string> = {
  stage: "stage",
  methodDisplay: "method",
  elevation: "elevation",
  occurredOnDisplay: "occurredOn",
};

const mapRaisingHistorySortToApi = (
  sort: RaisingHistoryTableSort | null,
): Pick<AdminStructureRaisingHistoryFilters, "sortBy" | "sortOrder"> => {
  if (!sort) return {};

  const sortBy = RAISING_HISTORY_COLUMN_TO_API_SORT[sort.key];
  if (!sortBy) return {};

  return {
    sortBy,
    sortOrder: sort.direction as "asc" | "desc",
  };
};

const METHOD_LABEL_KEYS: Record<DamRaisingMethod, string> = {
  downstream: "admin.structures.raisingHistory.method.downstream",
  upstream: "admin.structures.raisingHistory.method.upstream",
  centerLine: "admin.structures.raisingHistory.method.centerLine",
};

const formatOccurredOn = (iso: string): string => {
  if (!iso) return "—";
  const [year, month, day] = iso.split("-");
  if (!year || !month || !day) return iso;
  return `${day}/${month}/${year}`;
};

const formatRaisingCount = (count: number): string =>
  String(count).padStart(2, "0");

const emptyFormValues = (): RaisingHistoryFormValues => ({
  executiveProjectDesigner: "",
  stage: "",
  method: "downstream",
  elevation: "",
  occurredOn: "",
});

const toFormValues = (
  row: AdminStructureRaisingHistoryListItem,
): RaisingHistoryFormValues => ({
  executiveProjectDesigner: row.executiveProjectDesigner,
  stage: row.stage,
  method: row.method,
  elevation: row.elevation,
  occurredOn: row.occurredOn,
});

export type AdminStructureRaisingHistoryTabProps = {
  complexId: string;
  structureId: string;
  canWrite: boolean;
};

const AdminStructureRaisingHistoryTab = ({
  complexId,
  structureId,
  canWrite,
}: AdminStructureRaisingHistoryTabProps) => {
  const { translate } = useLocalization();
  const { user: me } = useUserContext();
  const updateTableUserPreferences = useUpdateTableUserPreferences(me?.userPreferences);
  const [searchParams, setSearchParams] = useSearchParams();

  const [appliedFilters, setAppliedFilters] =
    useState<AdminStructureRaisingHistoryFilters>({});
  const [tableSort, setTableSort] = useState<RaisingHistoryTableSort | null>(
    null,
  );
  const page = Math.max(
    1,
    Number(searchParams.get(RAISING_HISTORY_PAGE_PARAM) ?? DEFAULT_PAGE),
  );
  const limit = parsePageSize(searchParams.get(RAISING_HISTORY_LIMIT_PARAM));

  const queryFilters = useMemo(
    () => ({
      ...appliedFilters,
      page,
      limit,
      ...mapRaisingHistorySortToApi(tableSort),
    }),
    [appliedFilters, limit, page, tableSort],
  );

  const { data } = useAdminStructureRaisingHistoriesQuery(
    complexId,
    structureId,
    queryFilters,
  );
  const createMutation = useCreateAdminStructureRaisingHistoryMutation();
  const patchMutation = usePatchAdminStructureRaisingHistoryMutation();
  const deleteMutation = useDeleteAdminStructureRaisingHistoryMutation();

  const [modalOpen, setModalOpen] = useState(false);
  const [editingRow, setEditingRow] =
    useState<AdminStructureRaisingHistoryListItem | null>(null);
  const [deleteTarget, setDeleteTarget] =
    useState<AdminStructureRaisingHistoryListItem | null>(null);

  const raisingCount = data?.meta.raisingCount ?? 0;

  const resetRaisingHistoryPage = useCallback(() => {
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      next.set(RAISING_HISTORY_PAGE_PARAM, String(DEFAULT_PAGE));
      return next;
    });
  }, [setSearchParams]);

  const handleSelectedSortKey = useCallback<
    Dispatch<SetStateAction<RaisingHistoryTableSort>>
  >(
    (value) => {
      setTableSort((current) => {
        const next =
          typeof value === "function" ? value(current ?? { key: "", direction: "asc" }) : value;
        return next;
      });
      resetRaisingHistoryPage();
    },
    [resetRaisingHistoryPage],
  );

  const openCreateModal = () => {
    setEditingRow(null);
    setModalOpen(true);
  };

  const openEditModal = useCallback((row: AdminStructureRaisingHistoryListItem) => {
    setEditingRow(row);
    setModalOpen(true);
  }, []);

  const closeModal = () => {
    setModalOpen(false);
    setEditingRow(null);
  };

  const onSubmit = async (values: RaisingHistoryFormValues) => {
    if (editingRow) {
      await patchMutation.mutateAsync({
        complexId,
        structureId,
        raisingHistoryId: editingRow.id,
        payload: values,
      });
    } else {
      await createMutation.mutateAsync({
        complexId,
        structureId,
        payload: values,
      });
    }
    closeModal();
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    await deleteMutation.mutateAsync({
      complexId,
      structureId,
      raisingHistoryId: deleteTarget.id,
    });
    setDeleteTarget(null);
  };

  const handleFilter = (filters: AdminStructureRaisingHistoryFilters) => {
    setAppliedFilters(filters);
    resetRaisingHistoryPage();
  };

  const handleClearFilter = () => {
    setAppliedFilters({});
    resetRaisingHistoryPage();
  };

  const isSubmitting = createMutation.isPending || patchMutation.isPending;

  const tableData = useMemo(
    () =>
      (data?.data ?? []).map((item) => ({
        ...item,
        methodDisplay: translate(METHOD_LABEL_KEYS[item.method]),
        occurredOnDisplay: formatOccurredOn(item.occurredOn),
      })),
    [data?.data, translate],
  );

  const createActionMenu = useCallback(
    (rowData: { rowData: AdminStructureRaisingHistoryListItem }) => (
      <AdminStructureRaisingHistoryActionMenu
        row={rowData.rowData}
        onEdit={openEditModal}
        onDelete={setDeleteTarget}
      />
    ),
    [openEditModal],
  );

  const modalFormKey = editingRow?.id ?? "create";
  const modalDefaultValues = editingRow
    ? toFormValues(editingRow)
    : emptyFormValues();

  const tableColumns = useMemo(
    () => [
      {
        key: "stage",
        label: translate("admin.structures.raisingHistory.label.stage"),
        minWidth: "100px",
        sortType: "text" as const,
      },
      {
        key: "methodDisplay",
        label: translate("admin.structures.raisingHistory.label.method"),
        minWidth: "140px",
        sortType: "text" as const,
      },
      {
        key: "elevation",
        label: translate("admin.structures.raisingHistory.label.elevationShort"),
        minWidth: "120px",
        sortType: "number" as const,
      },
      {
        key: "occurredOnDisplay",
        label: translate("admin.structures.raisingHistory.label.occurredOn"),
        minWidth: "120px",
        sortType: "date" as const,
      },
      {
        key: "executiveProjectDesigner",
        label: translate(
          "admin.structures.raisingHistory.label.executiveProjectDesigner",
        ),
        minWidth: "220px",
      },
    ],
    [translate],
  );

  return (
    <div className="flex flex-col gap-4">
      <label className="flex max-w-[538px] flex-col gap-1">
        <span className="text-sm">
          {translate("admin.structures.raisingHistory.label.raisingCount")}
        </span>
        <InputBase
          type="text"
          value={formatRaisingCount(raisingCount)}
          readOnly
          tabIndex={-1}
          className={readOnlyInputClass}
          aria-label={translate(
            "admin.structures.raisingHistory.label.raisingCount",
          )}
        />
      </label>

      <div className="admin-structure-raising-history-table">
        <Table<AdminStructureRaisingHistoryListItem>
          userId={me?.id ?? ""}
          tableId={RAISING_HISTORY_TABLE_ID}
        usersPreferencesFromServer={me?.userPreferences}
        updateUsersPreferencesOnServer={updateTableUserPreferences}
        useSearchParamsHook={useSearchParams}
        setSelectedSortKey={handleSelectedSortKey}
        draggable
        enableTableColumnsSelector
        TableActionMenu={
          canWrite ? (createActionMenu as ComponentType) : undefined
        }
        TableButtons={
          canWrite ? (
            <Button
              size="small"
              variant="primary"
              onClick={openCreateModal}
              iconLeft={<PlusCircle />}
            >
              {translate("admin.structures.raisingHistory.button.register")}
            </Button>
          ) : undefined
        }
        TableFilter={() => (
          <AdminStructureRaisingHistoryFilter
            onFilter={handleFilter}
            onClear={handleClearFilter}
            initialFilters={{
              stage: appliedFilters.stage,
              method: appliedFilters.method,
            }}
          />
        )}
        TablePaginator={
          <Gs3TablePaginator
            meta={data?.meta ?? EMPTY_PAGINATION_META}
            useUrlState
            pageParamName={RAISING_HISTORY_PAGE_PARAM}
            limitParamName={RAISING_HISTORY_LIMIT_PARAM}
            useSearchParamsHook={useSearchParams}
          />
        }
        filterButtonText={translate(
          "admin.structures.raisingHistory.table.filterButtonText",
        )}
        handleClickRow={() => {}}
        tableColumns={tableColumns}
        tableData={
          tableData as { [key: string]: unknown; color?: string }[]
        }
        />
      </div>

      <Modal
        open={modalOpen}
        onClose={closeModal}
        title={translate(
          editingRow
            ? "admin.structures.raisingHistory.modal.editTitle"
            : "admin.structures.raisingHistory.modal.createTitle",
        )}
        content={
          <AdminStructureRaisingHistoryForm
            key={modalFormKey}
            defaultValues={modalDefaultValues}
            isSubmitting={isSubmitting}
            onSubmit={onSubmit}
            onCancel={closeModal}
          />
        }
      />

      <DeleteModal
        open={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        isDeleting={deleteMutation.isPending}
        itemDescription={deleteTarget?.stage}
        title={translate("admin.structures.raisingHistory.modal.deleteTitle")}
        message={translate("admin.structures.raisingHistory.modal.deleteMessage")}
        alertMessage={translate("common.deleteModalAlertMessage")}
        confirmButtonText={translate("admin.structures.raisingHistory.button.confirm")}
        cancelButtonText={translate("common.cancel")}
        deletingButtonText={translate("common.deleting")}
      />
    </div>
  );
};

export default AdminStructureRaisingHistoryTab;
